import { Component, AfterViewInit, ViewChild, ElementRef, OnDestroy, Input, Renderer2, AfterViewChecked, EventEmitter, Output } from '@angular/core';
import * as Highcharts from 'highcharts';
import { IPrecalData } from 'src/app/interfaces/iprecal-data';
import { IReportGroups } from 'src/app/interfaces/ireport-groups';
import { IActiveReportGroups } from 'src/app/interfaces/iactive-report-groups';
import { ReportingService } from 'src/app/services/reporting/reporting.service';
import { Router } from '@angular/router';
import { ResponseTimeService } from 'src/app/services/general/response-time.service';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { TranslationService } from 'src/app/services/translation.service';
import { TRANSLOCO_SCOPE, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-reporting-engagement',
  templateUrl: './reporting-engagement.component.html',
  styleUrls: ['./reporting-engagement.component.scss'],
  standalone: false,
  providers: [
      {
        provide: TRANSLOCO_SCOPE,
        useValue: 'reporting',
      },
    ],
})
export class ReportingEngagementComponent implements AfterViewInit, OnDestroy, AfterViewChecked {
  @Output() resultFetched: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() moduleName: string | undefined;
  @Input() moduleUuid: string = '';
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;
  reportGroups: IReportGroups[] = [];
  isGroupLoading: boolean = true;
  isPreCalLoading: boolean = true;
  showBreadCrums: boolean = false;
  chart: Highcharts.Chart | undefined;
  activeReportGroup: string = '';
  chartDrawn: boolean = false;
  reportGroupUuid: string = '';
  maxColumnWidth = 76; // Maximum width for each column
  precalData: IPrecalData | undefined;
  globalPointIndex: number = -1;
  activeReportGroups: IActiveReportGroups[] = [];
  selectedReportGroup!: IReportGroups;
  noDataMsg: string = '';
  chartOptions!: Highcharts.Options;
  isReportGroupDataAvailable: boolean = false;

  constructor(private renderer: Renderer2,
    private reportingService: ReportingService,
    private router: Router,
    private responseTimeService: ResponseTimeService,
    private amplitudeService: AmplitudeService,
    private translationService: TranslationService,
    private translocoService: TranslocoService
  ) { }

  ngAfterViewChecked() {
    if (!this.isGroupLoading && !this.isPreCalLoading && this.chartContainer && !this.chartDrawn && this.isReportGroupDataAvailable) {
      this.drawChart();
      this.chartDrawn = true;
    }
  }

  ngOnInit() {
    this.translationService.loadScopedTranslation('reporting');
    this.chartOptions = this.getInitialChartOptions();
    this.getReportGroups();
  }

  ngAfterViewInit() {
    this.drawChart();
    document.addEventListener('click', this.handleClickOutside.bind(this));
    this.renderer.listen('document', 'click', (event) => {
      if (event.target && (event.target as HTMLElement).classList.contains('expand-group')) {
        if (this.globalPointIndex > -1) {
          this.onPointClick(this.chart!.series[0].data[+this.globalPointIndex].index);
        }
      }
      if (event.target && (event.target as HTMLElement).classList.contains('open-my-teams')) {
        this.router.navigate(['/team_manager/my_teams']);
      }
    });
  }

  ngOnDestroy() {
    document.removeEventListener('click', this.handleClickOutside.bind(this));
    this.chart?.destroy();
  }

  getInitialChartOptions(): Highcharts.Options {
    const translationService = this.translocoService; // Capture translationService in a closure
    return {
      chart: {
        type: 'column',
        events: {
          load: this.adjustColumnWidth.bind(this),
          redraw: this.adjustColumnWidth.bind(this),
          click: () => this.onChartClick()
        }
      },
      title: { text: '' },
      xAxis: { categories: [] },
      yAxis: {
        min: 0,
        max: 100,
        title: { text: '' },
        labels: { format: '{value}%' },
        tickPositions: [0, 25, 50, 75, 100],
      },
      legend: { enabled: false },
      plotOptions: {
        series: {
          cursor: 'pointer',
          stacking: 'percent',
          point: {
            events: {
              click: (event) => this.onPointClick(event.point.index as number),
              mouseOver: (event) => this.onPointHover(event as Highcharts.PointerEventObject)
            }
          }
        },
        column: {
          pointPadding: 0.1,
          groupPadding: 0.1,
          maxPointWidth: this.maxColumnWidth
        }
      },
      series: [
        { name: 'notStarted', data: [], color: 'rgba(255, 153, 0, 0.4)', type: 'column' },
        { name: 'inProgress', data: [], color: 'rgba(59, 175, 240, 0.4)', type: 'column' },
        { name: 'certified', data: [], color: 'rgba(72, 186, 124, 0.4)', type: 'column' },
      ],
      tooltip: {
        useHTML: true,
        shared: true,
        stickOnContact: true,
        outside: true,
        formatter: function () {
          const points = this.points as Highcharts.TooltipFormatterContextObject[];
          const firstPoint = points && points.length > 0 ? points[0] : null;
          const pointIndex = firstPoint ? firstPoint.point.index : -2;

          // Access the precalData attached to the chart instance
          const chart = firstPoint?.series.chart;
          const precalData = (chart as any).precalData;
          const childData = precalData?.children?.[pointIndex];
          const totalUserAssignmentCount = childData ? childData.totalUserAssignmentCount : 0;
          

          function getFormattedSeriesName(seriesName: string): string {
            const nameMap: { [key: string]: string } = {
              certified: translationService.translate('CERTIFIED', {}, 'reporting'),
              notStarted: translationService.translate('NOT_STARTED', {}, 'reporting'),
              inProgress: translationService.translate('IN_PROGRESS', {}, 'reporting')
            };
            return nameMap[seriesName] || seriesName;
          }
          return `<div id="custom-tooltip" class="custom-tooltip">
                    <div class="tooltip-arrow"></div>
                    <div class="tooltip-content">
                      <b>${this.x}</b><br/>
                      <div class="learner-info">${totalUserAssignmentCount} ${totalUserAssignmentCount === 1 ? translationService.translate('USER', {}, 'reporting') : translationService.translate('USERS', {}, 'reporting')}</div>
                      <div class="point-container">
                        ${points.map(point => {
            const seriesOptions = point.series.options as Highcharts.SeriesColumnOptions;
            let hoverColor = point.color;
            if (seriesOptions.states?.hover && typeof seriesOptions.states.hover !== 'number') {
              hoverColor = seriesOptions.states.hover.color || point.color;
            }
            // Get the numberOfMembers for the current point
            const numberOfMembers = childData ? childData.progressOverview[point.series.name].numberOfMembers : 0;

            return `
                            <div class="point-container">
                              <span class="point-color" style="background-color:${hoverColor};"></span> 
                              <div class="point-label">${numberOfMembers} ${getFormattedSeriesName(point.series.name)}</div>
                            </div>
                          `;
          }).join('')}
                      </div>
                      ${childData?.hasChild ?
              `<div class="link expand-group">
                           ${translationService.translate('EXPAND_GROUP', {}, 'reporting')} <img class="expand-group" src="/assets/icons/expand-group.svg" alt="${translationService.translate('EXPAND_GROUP', {}, 'reporting')}" />
                         </div>`
              :
              `<div class="link open-my-teams">
                           ${translationService.translate('TEAM_LINK_LABEL', {}, 'reporting')} <img class="open-my-teams" src="/assets/icons/arrow/qnt-open-new-tab.svg" alt="${translationService.translate('TEAM_LINK_LABEL', {}, 'reporting')}" />
                         </div>`
            }
                    </div>
                  </div>`;
        },
        positioner: function (labelWidth, labelHeight, point) {
          const chart = this.chart;
          const yOffset = 10;
          const yPositionAbove = point.plotY + chart.plotTop - labelHeight - yOffset;
          const yPosition = yPositionAbove + 10;

          return {
            x: point.plotX + chart.plotLeft - labelWidth / 2,
            y: yPosition,
          };
        },
        backgroundColor: 'white',
        borderColor: 'white',
        borderRadius: 5,
        borderWidth: 1,
        shadow: true,
      },
      credits: { enabled: false },
      accessibility: {
        enabled: false,
      }

    };
  }

  handleExpandGroupClick(pointIndex: number) {
    if (this.chart) {
      const point = this.chart.series[0].data[pointIndex];
      this.onPointClick(point.index);
    }
  }

  onPointHover(point: any) {
    const pointIndex = point.target.index;
    this.globalPointIndex = pointIndex;
  }

  setGroup(group: IReportGroups) {
    this.reportGroups.forEach(g => g.active = g.reportGroupName === group.reportGroupName);
    this.setActiveReportGroup(group);
  }

  setActiveReportGroup(reportGroup: IReportGroups) {
    this.activeReportGroup = reportGroup.reportGroupName;
    this.reportGroupUuid = reportGroup.reportGroupUuid;
    this.activeReportGroups = [];
    this.activeReportGroups.push({
      reportGroupName: reportGroup.reportGroupName,
      reportGroupUuid: reportGroup.reportGroupUuid,
      active: true,
      nestedGroups: []
    });
    this.shouldShowBreadcrums();
    if(this.isReportGroupDataAvailable) {
      this.reportGroupChildData();
    }
  }

  getReportGroups() {
    // Account UUID
    const selectedAccountInfo = localStorage.getItem('selected_account_info');
    const currentAccountUuid = localStorage.getItem('current_account_uuid');
    const accountUuid = selectedAccountInfo ? JSON.parse(selectedAccountInfo).uuid : currentAccountUuid;
    //
    this.noDataMsg = '';
    this.isReportGroupDataAvailable = true;
    this.responseTimeService.logResponseTime(
      this.reportingService.getReportGroups(accountUuid, this.moduleUuid),
      'getReportGroups'
    ).subscribe({
      next: (res: IReportGroups[]) => {
        this.isGroupLoading = false;
        if (!res || Object.keys(res).length === 0) {
          this.noDataMsg = this.translocoService.translate('NO_DATA_FOUND', {}, 'reporting');
          this.isReportGroupDataAvailable = false;
          this.isPreCalLoading = false;
        }
        this.reportGroups = res;
        this.reportGroups.forEach(group => {
          group.active = group.reportSequence === 1;
          group.active ? this.setActiveReportGroup(group) : '';
        });
        this.selectedReportGroup = this.reportGroups.find(group => group.active) || this.reportGroups[0];
      },
      error: (error) => {
        console.error('Error fetching report groups', error);
      }
    });
  }

  reportGroupChildData() {
    this.isPreCalLoading = true;
    this.noDataMsg = '';
    // Account UUID
    const selectedAccountInfo = localStorage.getItem('selected_account_info');
    const currentAccountUuid = localStorage.getItem('current_account_uuid');
    const accountUuid = selectedAccountInfo ? JSON.parse(selectedAccountInfo).uuid : currentAccountUuid;
    //
    this.responseTimeService.logResponseTime(
      this.reportingService.getPrecalcReportGroupChildData(accountUuid, this.moduleUuid, this.reportGroupUuid),
      'getPrecalcReportGroupChildData'
    ).subscribe({
      next: (res: IPrecalData) => {
        this.isPreCalLoading = false;
        if (!res || Object.keys(res).length === 0) {
          this.noDataMsg = this.translocoService.translate('NO_DATA_FOUND', {}, 'reporting');
        }
        this.precalData = res;
        this.updateChartOptions();
        this.resultFetched.emit(true);
      },
      error: (error) => {
        this.isPreCalLoading = false;
        console.error('Error fetching report groups', error);
      }
    });
  }

  updateChartOptions() {
    if (this.precalData && this.precalData.children.length > 0) {
      this.chartOptions.xAxis = {
        categories: this.precalData.children.map(child =>
          child.hasChild ? `${child.displayString}*` : child.displayString
        )
      };

      this.chartOptions.series = [
        this.createSeries('notStarted', this.precalData.children.map(child => child.progressOverview.notStarted.percentage), 'rgba(255, 153, 0, 0.4)', 'rgba(255, 153, 0, 1)'),
        this.createSeries('inProgress', this.precalData.children.map(child => child.progressOverview.inProgress.percentage), 'rgba(59, 175, 240, 0.4)', 'rgba(59, 175, 240, 1)'),
        this.createSeries('certified', this.precalData.children.map(child => child.progressOverview.certified.percentage), 'rgba(72, 186, 124, 0.4)', 'rgba(72, 186, 124, 1)')
      ];
    } else {
      this.noDataMsg = this.translocoService.translate('NO_DATA_FOUND', {}, 'reporting');
      this.chartOptions.xAxis = {
        categories: []
      };
      this.chartOptions.series = [
        this.createSeries('notStarted', [], 'rgba(255, 153, 0, 0.4)', 'rgba(255, 153, 0, 1)'),
        this.createSeries('inProgress', [], 'rgba(59, 175, 240, 0.4)', 'rgba(59, 175, 240, 1)'),
        this.createSeries('certified', [], 'rgba(72, 186, 124, 0.4)', 'rgba(72, 186, 124, 1)')
      ];
    }
    this.drawChart();
  }

  createSeries(name: string, data: number[], color: string, hoverColor: string): Highcharts.SeriesColumnOptions {
    return {
      name,
      data,
      color,
      type: 'column',
      states: {
        hover: {
          color: hoverColor
        }
      }
    } as Highcharts.SeriesColumnOptions;
  }

  drawChart() {
    const scrollPosition = window.scrollY;
    if (this.chartContainer) {
      this.chart = Highcharts.chart(this.chartContainer.nativeElement, this.chartOptions) as Highcharts.Chart;
      (this.chart as any).precalData = this.precalData;
      if (this.chart && this.chart.series) {
        this.chart.series.forEach(series => {
          if (series.points) {
            series.points.forEach(point => {
              const childData = this.precalData?.children?.[point.index];
              if (childData && childData.hasChild) {
                point.graphic?.attr({ cursor: 'pointer' });
              } else {
                point.graphic?.attr({ cursor: 'default' });
              }
            });
          }
        });
      }
    } else {
      console.log("chart not found");
    }
    window.scrollTo(0, scrollPosition);
  }

  adjustColumnWidth() {
    if (this.chart && this.chartOptions.xAxis) {
      const xAxisOptions = this.chartOptions.xAxis as Highcharts.XAxisOptions;
      const chartWidth = this.chart.plotWidth;
      const numCategories = xAxisOptions.categories?.length || 0;

      if (this.chart.series) {
        const pointWidth = Math.min(this.maxColumnWidth, chartWidth / numCategories - 10);

        let needsRedraw = false;
        this.chart.series.forEach(series => {
          const seriesOptions = series.options as Highcharts.SeriesColumnOptions;
          if (seriesOptions.pointWidth !== pointWidth) {
            series.update({
              type: 'column',
              pointWidth: pointWidth > this.maxColumnWidth ? this.maxColumnWidth : pointWidth
            }, false);
            needsRedraw = true;
          }
        });

        if (needsRedraw) {
          this.chart.redraw();
        }
      }
    }
  }

  onPointClick(index: number) {
    const selectedGroup = this.reportGroups.find(group => group.reportGroupUuid === this.reportGroupUuid);
    if (selectedGroup) {
      this.selectedReportGroup = selectedGroup;
    }
    const childData = this.precalData?.children?.[index];
    // Sending the Event of Clicking on a Certification Bar to Amplitude
    this.amplitudeService.trackEvent('modulereport_click_certificationsbar', {
      'group_name': childData?.displayString,
    });

    if (childData && childData.hasChild && !this.isPreCalLoading) {
      this.reportGroupUuid = childData.reportGroupUuid;
      const activeGroup = this.activeReportGroups.find(group => group.active);
      if (activeGroup) {
        activeGroup.nestedGroups?.push({
          reportGroupName: childData.displayString,
          reportGroupUuid: childData.reportGroupUuid,
          active: true,
          reportSequence: 1
        });
      }
      this.shouldShowBreadcrums();
      this.reportGroupChildData();
    }

  }

  onChartClick() {
    this.chart?.tooltip.hide(0);
  }

  handleClickOutside(event: MouseEvent) {
    const tooltipElement = document.querySelector('.custom-tooltip');
    const target = event.target as Node;

    if (tooltipElement && !tooltipElement.contains(target) && this.chartContainer.nativeElement && !this.chartContainer.nativeElement.contains(target)) {
      if (this.chart && this.chart.tooltip) {
        this.chart.tooltip.hide(0);
      }
    }
  }

  shouldShowGroup(): boolean {
    return this.precalData?.children.some(child => child.hasChild) ?? false;
  }

  shouldShowBreadcrums(): boolean {
    this.showBreadCrums = this.activeReportGroups.some(group => group.nestedGroups && group.nestedGroups.length > 0);
    return this.showBreadCrums;
  }

  resetGroup(reportGroupUuid: string) {
    const selectedGroup = this.reportGroups.find(group => group.reportGroupUuid === reportGroupUuid);
    if (selectedGroup) {
      this.selectedReportGroup = selectedGroup;
      this.setGroup(selectedGroup);
    }
  }

  pruneNestedGroup(group: IActiveReportGroups) {
    this.reportGroupUuid = group.reportGroupUuid;
    this.reportGroupChildData();
    const nestedGroups = this.activeReportGroups[0].nestedGroups;
    if (nestedGroups) {
      const index = nestedGroups.findIndex(g => g.reportGroupUuid === group.reportGroupUuid);
      if (index !== -1) {
        nestedGroups.splice(index + 1);
      }
    }
  }

  formatNumber(value?: number): string {
    if (!value) {
      return '';
    }
    const truncatedValue = Math.floor(value * 10) / 10;
    return truncatedValue.toFixed(1);
  }
}